import { canonicalZone, YOM_TOV_MINHAGIM } from '@app/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  countryName,
  openPlace,
  placeToSend,
  postalLabel,
} from '../lib/address-place'
import { useTRPC } from '../lib/trpc'
import { isZoneName, useZoneOptions } from '../lib/zones'
import { AddressPlace } from './AddressPlace'
import {
  Button,
  Field,
  FieldRow,
  FieldSection,
  Notice,
  TextInput,
} from './index'
import { Select } from './Select'

/**
 * Where this person is, which decides when their day of rest is.
 *
 * It sits beside the rollups because that is what it acts on: nothing here is
 * sent on Shabbos or Yom Tov, and working out when those fall needs a sunset,
 * which a time zone cannot give. Stating an address is what makes the answer
 * exact; without one it is worked out from the zone and deliberately widened,
 * which the section says rather than leaves somebody to discover.
 *
 * A draft with its own commit, unlike the choices above, which take effect as
 * they are given: a half-typed address is not a value, and looking it up is
 * part of saving it rather than a second act.
 */
interface PlaceDraft {
  addressLine: string
  city: string
  region: string
  postalCode: string
  country: string
  timeZoneId: string
  yomTovMinhag: string
}

/** The fields the geocoder reads. What is not one of these needs no lookup. */
const ADDRESS_KEYS = [
  'addressLine',
  'city',
  'region',
  'postalCode',
  'country',
] as const satisfies readonly (keyof PlaceDraft)[]

/**
 * How many days of Yom Tov this person keeps.
 *
 * ASKED, never worked out from where they are. Somebody from outside Israel
 * keeps two days while they are in it, so geography answers wrongly for exactly
 * the people most likely to be somewhere other than where they are from — and
 * it answers wrongly in silence, on the days it matters most.
 *
 * No answer means the place decides, and that is the ABSENCE of a minhag rather
 * than a member of the list: one state, one way to say it. It is also where
 * everybody starts, so nothing about anybody's Yom Tov moves by this existing.
 */
const MINHAG_LABELS: Record<(typeof YOM_TOV_MINHAGIM)[number], string> = {
  one_day: 'One day',
  two_days: 'Two days',
}

/** No stated minhag, carried through the draft the way every blank here is. */
const FOLLOWS_PLACE = ''

/*
 * Both lists below come from the runtime's own `Intl`, and both are guarded.
 *
 * This sheet is drawn by the SHELL rather than by a route, so it sits outside
 * every route's error boundary: a constructor or a method the browser does not
 * have throws during render and replaces the whole application, and navigating
 * does not reset it. A field that offers less than it should is the smaller
 * failure by a wide margin, so each list degrades on its own terms — a country
 * keeps its code for a name, and the zone becomes a line somebody types.
 */

export function Place() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const place = useQuery(trpc.auth.place.queryOptions())
  const [draft, setDraft] = useState<PlaceDraft | null>(null)
  const [missed, setMissed] = useState(false)
  /* Looking the address up is part of saving, so it is part of what the commit
     is busy with — the mutation is not pending yet and the button must not be
     live, or a second press starts a second lookup against the same rate limit. */
  const [locating, setLocating] = useState(false)
  /* A typed zone is judged once the person has finished typing it, not while
     they are part way through the word. */
  const [zoneFocused, setZoneFocused] = useState(false)

  const zones = useZoneOptions()

  /*
   * The default option says what it currently comes out as.
   *
   * "Follow where I am" names a mechanism and leaves the reader to guess the
   * answer, which is the same silence that makes a derived minhag dangerous.
   * The resolved figure is only the FOLLOWED one while nothing is stated — once
   * somebody answers, it is their answer being reported back, so it is offered
   * against the saved value rather than the draft.
   */
  const minhagOptions = useMemo(() => {
    const following =
      place.data && place.data.yomTovMinhag === null
        ? place.data.keepsOneDay
          ? 'One day, where you are now.'
          : 'Two days, where you are now.'
        : undefined
    return [
      {
        value: FOLLOWS_PLACE,
        label: 'Follow where I am',
        description: following,
      },
      ...YOM_TOV_MINHAGIM.map((m) => ({
        value: m as string,
        label: MINHAG_LABELS[m],
      })),
    ]
  }, [place.data])

  const current: PlaceDraft = {
    addressLine: place.data?.addressLine ?? '',
    city: place.data?.city ?? '',
    region: place.data?.region ?? '',
    postalCode: place.data?.postalCode ?? '',
    country: place.data?.country ?? '',
    timeZoneId: place.data?.timeZoneId ?? '',
    yomTovMinhag: place.data?.yomTovMinhag ?? FOLLOWS_PLACE,
  }
  /* Opened with a stray state already cleared, so saving the fix is one
     press (`openPlace`). */
  const value = draft ?? openPlace(current)
  const set = (key: keyof PlaceDraft, v: string) => {
    /* A notice describes the save it came out of. The moment the draft moves on
       it is standing beside an address nobody has tried to save. */
    setMissed(false)
    setDraft({ ...value, [key]: v })
  }

  const save = useMutation(
    trpc.auth.setPlace.mutationOptions({
      onSuccess: () => {
        setDraft(null)
        qc.invalidateQueries({ queryKey: trpc.auth.place.queryKey() })
      },
    }),
  )

  const changed = (Object.keys(current) as (keyof PlaceDraft)[]).some(
    (k) => value[k] !== current[k],
  )

  /*
   * A zone nothing resolves is refused here rather than saved and discovered
   * later: with coordinates it would be used verbatim as the place's zone, and
   * without them it leaves the person unplaceable — a Shabbos nothing is held
   * for, with nothing on the screen saying why. The picker can only emit a real
   * zone, so this is about the typed line the degraded runtime offers.
   */
  const typedZone = value.timeZoneId.trim()
  const zoneUnknown = typedZone !== '' && !isZoneName(typedZone)
  /* Said while the person is not in the field: a half-typed name is not yet an
     answer, and a message that fires on every keystroke is one nobody reads. A
     zone already held that nothing resolves is said on sight, which is the
     point — nothing else on this screen would ever mention it. */
  const sayZone = zoneUnknown && !zoneFocused

  /* A save that touched only the minhag or the zone has nothing to look up: the
     address is the one already placed, and asking again spends the rate limit
     to arrive back where it started — or fails and reports an address nobody
     edited as one that could not be placed. */
  const addressChanged = ADDRESS_KEYS.some((k) => value[k] !== current[k])

  /*
   * The address is looked up as part of saving. Two buttons would ask somebody
   * to know that coordinates are a separate thing from the address they typed,
   * which is exactly what this screen exists to spare them.
   */
  const commit = async () => {
    setMissed(false)
    /* The country goes into the lookup by its name, since a town name repeats
       across the world and the code is not what a geocoder reads. */
    const query = [
      value.addressLine,
      value.city,
      value.region,
      value.postalCode,
      value.country ? countryName(value.country) : '',
    ]
      .map((p) => p.trim())
      .filter(Boolean)
      .join(', ')

    /*
     * What is written for the coordinates is one of three things, and they must
     * not be confused with each other. The server writes only the keys it is
     * passed, which is what lets the middle case be said at all.
     *
     * - An address that was placed writes the pair the lookup produced.
     * - An address the lookup could not answer for writes NEITHER key, so
     *   whatever is held stands. A rate limit, a dropped connection and a town
     *   the geocoder does not know all arrive here, and none of them is somebody
     *   saying where they are not — erasing an exact place turns their Shabbos
     *   into a zone-wide approximation held wide at both ends, without asking.
     * - An emptied address writes null for both, because that is a person
     *   clearing where they are. It is a deliberate act and it stays possible.
     *
     * The last two produce different payloads, so the row and anybody reading it
     * can tell a clearing from a failure.
     */
    let coordinates: { latitude?: number | null; longitude?: number | null } =
      addressChanged ? { latitude: null, longitude: null } : {}
    let unplaced = false
    if (query && addressChanged) {
      setLocating(true)
      try {
        const located = await qc.fetchQuery(
          trpc.auth.locatePlace.queryOptions({ query }),
        )
        coordinates = {
          latitude: located.latitude,
          longitude: located.longitude,
        }
      } catch {
        coordinates = {}
        unplaced = true
      } finally {
        setLocating(false)
      }
    }

    save.mutate(
      {
        addressLine: value.addressLine.trim() || null,
        city: value.city.trim() || null,
        postalCode: value.postalCode.trim() || null,
        /* The country and state only when they moved: a state off the list
           must not stop the rest being saved. */
        ...placeToSend(current, value),
        /* Null is the whole of "follow where I am" — the enum holds no member
           for it, so clearing is how it is said. The draft holds strings, as
           every field on it does, and the only thing that writes this one is
           the picker's own options; the server checks the value regardless. */
        yomTovMinhag:
          (value.yomTovMinhag as (typeof YOM_TOV_MINHAGIM)[number]) || null,
        ...coordinates,
        /* One spelling is stored, whichever the person typed. The server
           canonicalizes and checks it again — this is so what is sent is what
           the field was validated against. */
        ...(typedZone ? { timeZoneId: canonicalZone(typedZone) } : {}),
      },
      /* Said only once there is a save to say it about. Announced before the
         mutation answers, the sentence claims an address was kept on the runs
         where nothing was written at all. */
      { onSuccess: () => setMissed(unplaced) },
    )
  }

  return (
    <FieldSection
      title="Where you are"
      description="Nothing is sent to you on Shabbos or Yom Tov. Working out when those begin and end needs somewhere to put the sun."
    >
      <Field
        label="Address"
        info="Only ever used to work out when your Shabbos and Yom Tov begin and end. It is yours: no organization you belong to can see it."
      >
        <TextInput
          value={value.addressLine}
          onChange={(e) => set('addressLine', e.target.value)}
        />
      </Field>
      <FieldRow>
        <Field label="Town or city">
          <TextInput
            value={value.city}
            onChange={(e) => set('city', e.target.value)}
          />
        </Field>
        <Field label={postalLabel(value.country)}>
          <TextInput
            value={value.postalCode}
            onChange={(e) => set('postalCode', e.target.value)}
          />
        </Field>
      </FieldRow>
      <AddressPlace
        value={{ country: value.country, region: value.region }}
        held={{ country: current.country, region: current.region }}
        onValueChange={(p) => {
          setMissed(false)
          setDraft({ ...value, country: p.country, region: p.region })
        }}
      />
      <Field
        label="Time zone"
        info="It decides when your day starts — the hour a daily rollup arrives. On its own, with no address, it is also what your Shabbos is approximated from."
      >
        {/* A line to type where the runtime holds no list to pick from. It
            takes an IANA name, which the placeholder shows rather than
            describes. */}
        {zones ? (
          <Select
            value={value.timeZoneId || null}
            onValueChange={(zone) => set('timeZoneId', zone)}
            options={zones}
            placeholder="Choose a time zone"
          />
        ) : (
          <TextInput
            value={value.timeZoneId}
            placeholder="America/New_York"
            aria-invalid={sayZone}
            onChange={(e) => set('timeZoneId', e.target.value)}
            onFocus={() => setZoneFocused(true)}
            onBlur={() => setZoneFocused(false)}
          />
        )}
      </Field>
      {/* Beside the field it is about, so the person reads it where they typed
          rather than at the foot beside the commit. */}
      {sayZone ? (
        <Notice tone="error">
          That is not a time zone we recognize. It takes an IANA name, spelled
          the way the example is — America/New_York, Asia/Jerusalem.
        </Notice>
      ) : null}
      <Field
        label="Days of Yom Tov"
        info="Asked rather than worked out from your address: somebody from outside Israel keeps two days while they are in it, so where you are cannot answer this for you."
      >
        <Select
          value={value.yomTovMinhag}
          onValueChange={(minhag) => set('yomTovMinhag', minhag)}
          options={minhagOptions}
        />
      </Field>

      {place.data && !place.data.known ? (
        <Notice tone="error">
          We cannot tell where you are, so nothing is being held back for
          Shabbos. Add an address or a time zone.
        </Notice>
      ) : null}
      {place.data?.approximate ? (
        <Notice>
          Working from your time zone, so the times are approximate and held a
          little wider. An address makes them exact.
        </Notice>
      ) : null}
      {/* The address was kept and nothing about where you are was touched —
          which is what makes this different from the two notices above, and why
          it does not claim the zone is standing in. Whether it is depends on
          what was already held, and those notices say so themselves. */}
      {missed ? (
        <Notice tone="error">
          Saved, but that address could not be placed just now. Wherever we
          already had you stays as it is — try again in a moment, or add the
          town.
        </Notice>
      ) : null}
      {/* A failure stays beside the commit that failed. Nothing was written, so
          neither notice above is speaking for this run. */}
      {save.error ? <Notice tone="error">{save.error.message}</Notice> : null}

      <Button
        onClick={commit}
        disabled={!changed || zoneUnknown || locating || save.isPending}
      >
        {locating
          ? 'Looking it up…'
          : save.isPending
            ? 'Saving…'
            : 'Save where you are'}
      </Button>
    </FieldSection>
  )
}
